import { useCallback, useEffect, useRef, useState } from "react";
import { GetImages } from "../../../utils/GetImages";
import { IntegrationsService } from "@propertelligent/client-api";
import { TextCss } from "../../GlobalCss/TextCss";
import { getAddressComponentValue, CITY_COMPONENT_TYPE_PRIORITY } from "../../../utils/googleAddressComponents";

declare global {
  interface Window {
    google: any;
    initAutocomplete: () => void;
  }
}

const AddressAutocomplete = ({ setPropertyvalue, propertyValue, required = true, showSelectedValue = true }) => {
  const searchInput = useRef<HTMLInputElement>(null);
  const [apiKey, setApiKey] = useState("");

  useEffect(() => {
    IntegrationsService.postApiIntegrationsKeysGooglemaps()
      .then((res) => {
        setApiKey(res.success);
      })
      .catch((error) => {
        console.error("API error:", error);
      });
  }, []);

  const loadGoogleMaps = useCallback((apiKey: string) => {
    if (window.google?.maps) {
      initAutocomplete();
      return;
    }

    const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
    if (existingScript) {
      existingScript.addEventListener("load", initAutocomplete);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async&v=weekly&callback=initAutocomplete`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    window.initAutocomplete = initAutocomplete;
  }, []);

  const initAutocomplete = useCallback(() => {
    if (!searchInput.current || !window.google) return;

    const autocompleteInstance = new window.google.maps.places.Autocomplete(searchInput.current, {
      fields: ["address_components", "geometry"],
      componentRestrictions: { country: "us" },
    });

    autocompleteInstance.addListener("place_changed", () => {
      const place = autocompleteInstance.getPlace();
      //   setPropertyvalue({ ...propertyValue, address: extractAddress(place) });

      const { streetAddress, city, state, zip } = extractAddress(place);

      setPropertyvalue({
        ...propertyValue,
        streetAddress,
        city,
        state,
        zip
      });
    });

  }, [propertyValue, setPropertyvalue]);

  const extractAddress = (place: any) => {
    const address = {
      streetAddress: "",
      city: "",
      state: "",
      zip: "",
    };

    place?.address_components?.forEach((component: any) => {
      const types = component.types;
      const value = component.long_name;

      if (types.includes("street_number")) address.streetAddress = value;
      if (types.includes("route")) address.streetAddress += " " + value;
      if (types.includes("administrative_area_level_1")) address.state = value;
      if (types.includes("postal_code")) address.zip = value;
    });

    address.city = getAddressComponentValue(place?.address_components, CITY_COMPONENT_TYPE_PRIORITY);

    return address;
  };

  useEffect(() => {
    if (apiKey) loadGoogleMaps(apiKey);
  }, [apiKey, loadGoogleMaps]);

  return (
    <div className="AddressAutocomplete">
      <div className="search">
        <img src={GetImages.Search} alt="Search" className="iconstyle24" />
        <input
          className={TextCss.sub_heading_400_14}
          required={required}
          ref={searchInput}
          type="text"
          placeholder="Search Address..."
        />
        {searchInput?.current?.value && (
          <button
            onClick={() => {
              if (searchInput.current) searchInput.current.value = "";
              // setPropertyvalue({ ...propertyValue, address: { city: "", state: "", zip: "", streetAddress: "" } });
              setPropertyvalue({
                ...propertyValue,
                city: "",
                state: "",
                zip: "",
                streetAddress: ""
              });
            }}
          >
            <img src={GetImages.closeIconBlack} className="iconstyle24" alt="Close" />
          </button>
        )}
      </div>
      {showSelectedValue && searchInput?.current?.value && (
        <div className="address">
          <p className="showaddress">City: <span>{propertyValue?.address?.city}</span></p>
          <p className="showaddress">State: <span>{propertyValue?.address?.state}</span></p>
          <p>Zip: <span>{propertyValue?.address?.zip}</span></p>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
